//IssuesAndTaskDashboard.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Calendar } from '../ui';
import userService from '../../services/userService';
import {
  DelayAnalysisChart,
  IssuesRaisedSolvedChart,
  PendingIssuesChart,
  TrackIssueCompletionChart
} from './Charts';
import { issuesAndTasksApi } from './issuesAndTasksApi';

const IssuesAndTasksDashboard = () => {
  // Log screen view
  useScreenLogging('IssuesAndTasksDashboard', {
    component: 'IssuesAndTasksDashboard',
  });
  const navigate = useNavigate();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekRange, setWeekRange] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chart data states
  const [pendingIssuesPerSite, setPendingIssuesPerSite] = useState({});
  const [tasksPerSite, setTasksPerSite] = useState({});
  const [issuesPerSite, setIssuesPerSite] = useState({});
  const [delayTimePerSite, setDelayTimePerSite] = useState({});
  const [issuesRaisedPerSite, setIssuesRaisedPerSite] = useState({});
  const [issuesSolvedPerSite, setIssuesSolvedPerSite] = useState({});
  const [siteIdToName, setSiteIdToName] = useState({});
  const [isFnbTenant, setIsFnbTenant] = useState(false);

  let trackIssueTitle = 'Track and Issue Completion';
  let trackIssueYAxisLabel = 'Tasks and Issues';
  if (isFnbTenant) {
    trackIssueTitle = 'Issue Completion';
    trackIssueYAxisLabel = 'Issues';
  } else {
    trackIssueTitle = 'Track and Issue Completion';
    trackIssueYAxisLabel = 'Tasks and Issues';
  }

  useEffect(() => {
    const loadTenant = async () => {
      try {
        const isFnb = await userService.isFnbTenant();
        setIsFnbTenant(Boolean(isFnb));
      } catch (e) {
        setIsFnbTenant(false);
      }
    };
    loadTenant();
  }, []);

  // Get week start (Sunday) and end (Saturday) for a given date
  const getWeekRange = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day; // Sunday = 0
    startOfWeek.setDate(diff);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return { startOfWeek, endOfWeek };
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Fetch data on component mount and when date changes
  useEffect(() => {
    const { startOfWeek, endOfWeek } = getWeekRange(selectedDate);
    const weekRangeString = `${formatDate(startOfWeek)} to ${formatDate(endOfWeek)}`;
    setWeekRange(weekRangeString);
    
    const fetchDataAsync = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const dateStr = formatDateForAPI(selectedDate);

        // Fetch all required data in parallel
        const [sites, issuesData, taskIssueRes, delayRes, weeklyIssueRes] = await Promise.all([
          issuesAndTasksApi.fetchAllSites(),
          issuesAndTasksApi.getUnresolvedIssuesData(),
          issuesAndTasksApi.getTaskAndIssueResolutionData(dateStr),
          issuesAndTasksApi.getWeeklyAverageDelays(dateStr),
          issuesAndTasksApi.getWeeklyIssueData(dateStr),
        ]);

        // Map site IDs to names
        const siteIdToNameMap = {};
        sites.forEach(site => {
          siteIdToNameMap[site.site_id] = site.site_name;
        });

        // Pending Issues - Map site IDs to names
        const issuesWithNames = {};
        issuesData.forEach((count, siteId) => {
          const siteName = siteIdToNameMap[siteId] || siteId;
          issuesWithNames[siteName] = count;
        });

        // Tasks and Issues Completion
        const tasksPerSiteMap = {};
        const issuesPerSiteMap = {};
        if (taskIssueRes.per_site) {
          Object.entries(taskIssueRes.per_site).forEach(([siteId, data]) => {
            const siteName = siteIdToNameMap[siteId] || siteId;
            tasksPerSiteMap[siteName] = data.tasks_resolved || 0;
            issuesPerSiteMap[siteName] = data.issues_resolved || 0;
          });
        }

        // Delay Analysis (convert minutes to hours for chart)
        const delayTimePerSiteMap = {};
        if (delayRes.average_delay_minutes_per_site) {
          Object.entries(delayRes.average_delay_minutes_per_site).forEach(([siteId, minutes]) => {
            const siteName = siteIdToNameMap[siteId] || siteId;
            delayTimePerSiteMap[siteName] = minutes / 60.0; // Convert to hours
          });
        }

        // Issues Raised and Solved
        const issuesRaisedPerSiteMap = {};
        const issuesSolvedPerSiteMap = {};
        if (weeklyIssueRes.per_site) {
          Object.entries(weeklyIssueRes.per_site).forEach(([siteId, data]) => {
            const siteName = siteIdToNameMap[siteId] || siteId;
            issuesRaisedPerSiteMap[siteName] = data.raised || 0;
            issuesSolvedPerSiteMap[siteName] = data.solved || 0;
          });
        }

        // Update state
        setSiteIdToName(siteIdToNameMap);
        setPendingIssuesPerSite(issuesWithNames);
        setTasksPerSite(tasksPerSiteMap);
        setIssuesPerSite(issuesPerSiteMap);
        setDelayTimePerSite(delayTimePerSiteMap);
        setIssuesRaisedPerSite(issuesRaisedPerSiteMap);
        setIssuesSolvedPerSite(issuesSolvedPerSiteMap);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data. Please try again.');
        setIsLoading(false);
      }
    };
    
    fetchDataAsync();
  }, [selectedDate]);

  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const { startOfWeek, endOfWeek } = getWeekRange(date);
    const weekRangeString = `${formatDate(startOfWeek)} to ${formatDate(endOfWeek)}`;
    setWeekRange(weekRangeString);
    console.log('Selected week:', weekRangeString);
  };

  // Handle calendar toggle
  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  // Handle chart expand
  const handleChartExpand = (chartTitle) => {
    console.log(`Expanding chart: ${chartTitle}`);
    
    // Map chart titles to their zoom view routes
    const chartRoutes = {
      'Pending Issues': '/dashboard-ti-dashboard-zoomed-pending-issues',
      'Track and Issue Completion': '/dashboard-ti-dashboard-zoomed-track-issue-completion',
      'Delay Analysis': '/dashboard-ti-dashboard-zoomed-delay-analysis',
      'Issues Raised and solved': '/dashboard-ti-dashboard-zoomed-issues-raised-solved'
    };

    const route = chartRoutes[chartTitle];
    if (route) {
      navigate(route);
    }
  };

  // Prepare data for charts
  const pendingIssuesData = Object.values(pendingIssuesPerSite);
  const pendingIssuesLabels = Object.keys(pendingIssuesPerSite);
  
  const tasksIssuesData = Object.keys(tasksPerSite).map(key => ({
    tasks: tasksPerSite[key] || 0,
    issues: issuesPerSite[key] || 0
  }));
  const tasksIssuesLabels = Object.keys(tasksPerSite);

  const delayAnalysisData = Object.values(delayTimePerSite);
  const delayAnalysisLabels = Object.keys(delayTimePerSite);

  const issuesRaisedSolvedData = Object.keys(issuesRaisedPerSite).map(key => ({
    raised: issuesRaisedPerSite[key] || 0,
    solved: issuesSolvedPerSite[key] || 0
  }));
  const issuesRaisedSolvedLabels = Object.keys(issuesRaisedPerSite);

  return (
    <div className="w-full h-full">
      {/* Header Section */}
      <div className="mb-6">
        <p className={`${typography.p} text-black mb-2`}>
          Select the week to view the Analysis <span className={typography.p}>Across All Sites</span>
        </p>
      </div>
      
      {/* Week Selection Card */}
      <div 
        className="w-full max-w-md p-3 bg-neutral-50/50 rounded-xl inline-flex justify-between items-center cursor-pointer hover:bg-neutral-50/60 transition-colors mb-8"
        onClick={toggleCalendar}
      >
        <p className={`${typography.p} text-black`}>
          {weekRange || 'Loading...'}
        </p>
        <div className="w-6 h-6 relative overflow-hidden">
          {/* Calendar Icon */}
          <svg 
            className="w-5 h-5 text-black" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading data...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center justify-between">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => {
                const newDate = new Date(selectedDate);
                setSelectedDate(newDate);
              }}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Left - Pending Issues */}
          <PendingIssuesChart 
            data={pendingIssuesData}
            labels={pendingIssuesLabels}
            onExpand={() => handleChartExpand('Pending Issues')}
          />
          
          {/* Top Right - Track and Issue Completion */}
          <TrackIssueCompletionChart 
            data={tasksIssuesData}
            labels={tasksIssuesLabels}
            onExpand={() => handleChartExpand('Track and Issue Completion')}
            title={trackIssueTitle}
            yAxisLabel={trackIssueYAxisLabel}
          />
          
          {/* Bottom Left - Delay Analysis */}
          <DelayAnalysisChart 
            data={delayAnalysisData}
            labels={delayAnalysisLabels}
            onExpand={() => handleChartExpand('Delay Analysis')}
          />
          
          {/* Bottom Right - Issues Raised and solved */}
          <IssuesRaisedSolvedChart 
            data={issuesRaisedSolvedData}
            labels={issuesRaisedSolvedLabels}
            onExpand={() => handleChartExpand('Issues Raised and solved')}
          />
        </div>
      )}

      {/* Calendar Modal */}
      <Calendar
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        onDateSelect={handleDateSelect}
        selectedDate={selectedDate}
        className="w-[calc(100%-2rem)] max-w-sm sm:w-80"
      />
    </div>
  );
};

export default IssuesAndTasksDashboard;
